/**
 * Minimal QR Code encoder (ISO/IEC 18004), byte mode, error-correction level M.
 *
 * Written in-repo on purpose: the widget ships as a single IIFE embedded on
 * third-party pages, so it must not depend on a CDN QR image service (privacy,
 * offline, and CSP) nor pull a runtime dependency for one overlay.
 *
 * Scope: versions 1–13 at level M (up to 331 bytes), which comfortably covers a
 * configurator share URL. Anything longer throws instead of silently truncating.
 *
 * Level M rather than L: the symbol is scanned off a screen or a PDF viewer at
 * one or two device pixels per module, where the rasteriser smears module edges.
 * L's ~7% redundancy leaves no margin for that; M's ~15% does.
 */

// Per version (index = version - 1): total codewords, EC codewords per block,
// and the number of RS blocks. Level M only. Data codewords, block lengths and
// the group split are derived from these.
const VERSION_TOTAL_CODEWORDS = [
  26, 44, 70, 100, 134, 172, 196, 242, 292, 346, 404, 466, 532,
] as const;

const VERSION_EC_PER_BLOCK = [
  10, 16, 26, 18, 24, 16, 18, 22, 22, 26, 30, 22, 22,
] as const;

const VERSION_BLOCKS = [1, 1, 1, 2, 2, 4, 4, 4, 5, 5, 5, 8, 9] as const;

// Row/column centres of the alignment patterns, per version (v1 has none).
const ALIGNMENT_CENTERS: readonly (readonly number[])[] = [
  [],
  [6, 18],
  [6, 22],
  [6, 26],
  [6, 30],
  [6, 34],
  [6, 22, 38],
  [6, 24, 42],
  [6, 26, 46],
  [6, 28, 50],
  [6, 30, 54],
  [6, 32, 58],
  [6, 34, 62],
];

const MAX_VERSION = VERSION_TOTAL_CODEWORDS.length;
const PAD_CODEWORDS = [0xec, 0x11] as const;
const MASK_COUNT = 8;

// Level M, as placed in the format information bit string.
const EC_LEVEL_BITS = 0b00;

export type QRMatrix = {
  size: number;
  // Row-major, `true` = dark module.
  modules: boolean[][];
};

/** GF(256) log/antilog tables for the QR field (primitive polynomial 0x11d). */
const { exp: GF_EXP, log: GF_LOG } = (() => {
  const exp = new Uint8Array(512);
  const log = new Uint8Array(256);
  let x = 1;

  for (let i = 0; i < 255; i += 1) {
    exp[i] = x;
    log[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d;
  }
  for (let i = 255; i < 512; i += 1) {
    exp[i] = exp[i - 255];
  }

  return { exp, log };
})();

function gfMultiply(a: number, b: number): number {
  if (a === 0 || b === 0) return 0;
  return GF_EXP[GF_LOG[a] + GF_LOG[b]];
}

/** Reed-Solomon generator polynomial of the given degree, high term first. */
function generatorPolynomial(degree: number): number[] {
  let poly = [1];

  for (let i = 0; i < degree; i += 1) {
    const next = new Array<number>(poly.length + 1).fill(0);
    for (let j = 0; j < poly.length; j += 1) {
      next[j] ^= poly[j];
      next[j + 1] ^= gfMultiply(poly[j], GF_EXP[i]);
    }
    poly = next;
  }

  return poly;
}

function errorCorrectionCodewords(data: number[], ecCount: number): number[] {
  const generator = generatorPolynomial(ecCount);
  const remainder = new Array<number>(ecCount).fill(0);

  for (const byte of data) {
    const factor = byte ^ remainder[0];
    remainder.shift();
    remainder.push(0);
    if (factor !== 0) {
      for (let i = 0; i < ecCount; i += 1) {
        remainder[i] ^= gfMultiply(generator[i + 1], factor);
      }
    }
  }

  return remainder;
}

function dataCodewordCapacity(version: number): number {
  const index = version - 1;
  return (
    VERSION_TOTAL_CODEWORDS[index] -
    VERSION_EC_PER_BLOCK[index] * VERSION_BLOCKS[index]
  );
}

/** Bits used by the character count indicator in byte mode. */
function charCountBits(version: number): number {
  return version < 10 ? 8 : 16;
}

function pickVersion(byteLength: number): number {
  for (let version = 1; version <= MAX_VERSION; version += 1) {
    // 4 mode bits + count indicator + payload, rounded up to whole codewords.
    const bits = 4 + charCountBits(version) + byteLength * 8;
    if (bits <= dataCodewordCapacity(version) * 8) return version;
  }

  throw new Error(
    `[qr] Payload of ${byteLength} bytes exceeds the supported QR capacity.`,
  );
}

class BitBuffer {
  private bits: number[] = [];

  push(value: number, length: number) {
    for (let i = length - 1; i >= 0; i -= 1) {
      this.bits.push((value >>> i) & 1);
    }
  }

  get length(): number {
    return this.bits.length;
  }

  /** Pads to a byte boundary and returns the codewords. */
  toCodewords(): number[] {
    while (this.bits.length % 8 !== 0) this.bits.push(0);

    const codewords: number[] = [];
    for (let i = 0; i < this.bits.length; i += 8) {
      let byte = 0;
      for (let j = 0; j < 8; j += 1) byte = (byte << 1) | this.bits[i + j];
      codewords.push(byte);
    }

    return codewords;
  }
}

function buildDataCodewords(payload: Uint8Array, version: number): number[] {
  const capacity = dataCodewordCapacity(version);
  const buffer = new BitBuffer();

  // Byte mode, then the length, then the payload.
  buffer.push(0b0100, 4);
  buffer.push(payload.length, charCountBits(version));
  for (const byte of payload) buffer.push(byte, 8);

  // Terminator: up to four zero bits, truncated at the capacity.
  const remainingBits = capacity * 8 - buffer.length;
  buffer.push(0, Math.min(4, remainingBits));

  const codewords = buffer.toCodewords();
  // Pad bytes alternate starting from 0xEC, regardless of how many data
  // codewords precede them.
  for (let i = 0; codewords.length < capacity; i += 1) {
    codewords.push(PAD_CODEWORDS[i % 2]);
  }

  return codewords;
}

/** Splits the data into RS blocks and interleaves data + EC codewords. */
function interleaveCodewords(data: number[], version: number): number[] {
  const index = version - 1;
  const blockCount = VERSION_BLOCKS[index];
  const ecPerBlock = VERSION_EC_PER_BLOCK[index];
  const shortLength = Math.floor(data.length / blockCount);
  const longBlocks = data.length % blockCount;

  const dataBlocks: number[][] = [];
  const ecBlocks: number[][] = [];
  let offset = 0;

  for (let block = 0; block < blockCount; block += 1) {
    const length = shortLength + (block >= blockCount - longBlocks ? 1 : 0);
    const chunk = data.slice(offset, offset + length);
    offset += length;
    dataBlocks.push(chunk);
    ecBlocks.push(errorCorrectionCodewords(chunk, ecPerBlock));
  }

  const result: number[] = [];
  const longestData = shortLength + (longBlocks > 0 ? 1 : 0);

  for (let i = 0; i < longestData; i += 1) {
    for (const block of dataBlocks) {
      if (i < block.length) result.push(block[i]);
    }
  }
  for (let i = 0; i < ecPerBlock; i += 1) {
    for (const block of ecBlocks) result.push(block[i]);
  }

  return result;
}

type Grid = {
  size: number;
  // `null` = still free, so mask application can skip function patterns.
  cells: (boolean | null)[][];
};

function createGrid(size: number): Grid {
  return {
    size,
    cells: Array.from({ length: size }, () =>
      new Array<boolean | null>(size).fill(null),
    ),
  };
}

function setModule(grid: Grid, row: number, col: number, dark: boolean) {
  if (row < 0 || col < 0 || row >= grid.size || col >= grid.size) return;
  grid.cells[row][col] = dark;
}

function placeFinderPattern(grid: Grid, row: number, col: number) {
  for (let r = -1; r <= 7; r += 1) {
    for (let c = -1; c <= 7; c += 1) {
      const isPattern = r >= 0 && r <= 6 && c >= 0 && c <= 6;
      const onOuterRing = r === 0 || r === 6 || c === 0 || c === 6;
      const inCore = r >= 2 && r <= 4 && c >= 2 && c <= 4;
      // The -1/7 border is the separator: always light.
      setModule(grid, row + r, col + c, isPattern && (onOuterRing || inCore));
    }
  }
}

function placeAlignmentPatterns(grid: Grid, version: number) {
  const centers = ALIGNMENT_CENTERS[version - 1];

  for (const row of centers) {
    for (const col of centers) {
      // Skip the three corners already occupied by finder patterns.
      const nearFinder =
        (row === 6 && col === 6) ||
        (row === 6 && col === grid.size - 7) ||
        (row === grid.size - 7 && col === 6);
      if (nearFinder) continue;

      for (let r = -2; r <= 2; r += 1) {
        for (let c = -2; c <= 2; c += 1) {
          const dark = Math.max(Math.abs(r), Math.abs(c)) !== 1;
          setModule(grid, row + r, col + c, dark);
        }
      }
    }
  }
}

function placeTimingPatterns(grid: Grid) {
  for (let i = 8; i < grid.size - 8; i += 1) {
    const dark = i % 2 === 0;
    if (grid.cells[6][i] === null) setModule(grid, 6, i, dark);
    if (grid.cells[i][6] === null) setModule(grid, i, 6, dark);
  }
}

/** Reserves the format/version areas so data placement skips them. */
function reserveFormatAreas(grid: Grid, version: number) {
  for (let i = 0; i < 9; i += 1) {
    if (grid.cells[8][i] === null) setModule(grid, 8, i, false);
    if (grid.cells[i][8] === null) setModule(grid, i, 8, false);
  }
  for (let i = 0; i < 8; i += 1) {
    setModule(grid, 8, grid.size - 1 - i, false);
    setModule(grid, grid.size - 1 - i, 8, false);
  }
  // The dark module, fixed by the spec.
  setModule(grid, grid.size - 8, 8, true);

  if (version >= 7) {
    for (let i = 0; i < 18; i += 1) {
      const row = Math.floor(i / 3);
      const col = i % 3;
      setModule(grid, row, grid.size - 11 + col, false);
      setModule(grid, grid.size - 11 + col, row, false);
    }
  }
}

/** BCH-protected 15-bit format information for level M and the given mask. */
function formatInformation(mask: number): number {
  const data = (EC_LEVEL_BITS << 3) | mask;
  let remainder = data;

  for (let i = 0; i < 10; i += 1) {
    remainder = (remainder << 1) ^ ((remainder >>> 9) * 0x537);
  }

  return ((data << 10) | (remainder & 0x3ff)) ^ 0x5412;
}

/** BCH-protected 18-bit version information (versions 7+ only). */
function versionInformation(version: number): number {
  let remainder = version;

  for (let i = 0; i < 12; i += 1) {
    remainder = (remainder << 1) ^ ((remainder >>> 11) * 0x1f25);
  }

  return (version << 12) | (remainder & 0xfff);
}

function placeFormatInformation(grid: Grid, mask: number) {
  const bits = formatInformation(mask);

  for (let i = 0; i < 15; i += 1) {
    const dark = ((bits >>> i) & 1) === 1;

    // Copy around the top-left finder…
    if (i < 6) setModule(grid, i, 8, dark);
    else if (i === 6) setModule(grid, 7, 8, dark);
    else if (i === 7) setModule(grid, 8, 8, dark);
    else if (i === 8) setModule(grid, 8, 7, dark);
    else setModule(grid, 8, 14 - i, dark);

    // …and the redundant copy split across the other two.
    if (i < 8) setModule(grid, 8, grid.size - 1 - i, dark);
    else setModule(grid, grid.size - 15 + i, 8, dark);
  }
}

function placeVersionInformation(grid: Grid, version: number) {
  if (version < 7) return;
  const bits = versionInformation(version);

  for (let i = 0; i < 18; i += 1) {
    const dark = ((bits >>> i) & 1) === 1;
    const row = Math.floor(i / 3);
    const col = i % 3;
    setModule(grid, row, grid.size - 11 + col, dark);
    setModule(grid, grid.size - 11 + col, row, dark);
  }
}

/** Walks the two-module-wide columns, bottom-right to top-left. */
function placeDataBits(grid: Grid, codewords: number[]) {
  let bitIndex = 0;
  let upward = true;

  const nextBit = (): boolean => {
    const byte = codewords[bitIndex >>> 3];
    // Remainder modules past the last codeword stay light, per the spec.
    const dark =
      byte !== undefined && ((byte >>> (7 - (bitIndex & 7))) & 1) === 1;
    bitIndex += 1;
    return dark;
  };

  for (let right = grid.size - 1; right > 0; right -= 2) {
    // Column 6 is the vertical timing pattern: the pair shifts one to the left.
    const columnRight = right <= 6 ? right - 1 : right;

    for (let step = 0; step < grid.size; step += 1) {
      const row = upward ? grid.size - 1 - step : step;
      for (const col of [columnRight, columnRight - 1]) {
        if (grid.cells[row][col] === null) {
          grid.cells[row][col] = nextBit();
        }
      }
    }

    upward = !upward;
  }
}

function isMasked(mask: number, row: number, col: number): boolean {
  switch (mask) {
    case 0:
      return (row + col) % 2 === 0;
    case 1:
      return row % 2 === 0;
    case 2:
      return col % 3 === 0;
    case 3:
      return (row + col) % 3 === 0;
    case 4:
      return (Math.floor(row / 2) + Math.floor(col / 3)) % 2 === 0;
    case 5:
      return ((row * col) % 2) + ((row * col) % 3) === 0;
    case 6:
      return (((row * col) % 2) + ((row * col) % 3)) % 2 === 0;
    default:
      return (((row + col) % 2) + ((row * col) % 3)) % 2 === 0;
  }
}

/** Standard penalty score (rules 1–4); lower is better. */
function maskPenalty(modules: boolean[][]): number {
  const size = modules.length;
  let penalty = 0;
  let darkCount = 0;

  // Rule 1: runs of five or more same-coloured modules in a row or column.
  for (let i = 0; i < size; i += 1) {
    let rowRun = 1;
    let colRun = 1;

    for (let j = 1; j < size; j += 1) {
      rowRun = modules[i][j] === modules[i][j - 1] ? rowRun + 1 : 1;
      if (rowRun === 5) penalty += 3;
      else if (rowRun > 5) penalty += 1;

      colRun = modules[j][i] === modules[j - 1][i] ? colRun + 1 : 1;
      if (colRun === 5) penalty += 3;
      else if (colRun > 5) penalty += 1;
    }
  }

  // Rule 2: 2x2 blocks of one colour.
  for (let i = 0; i < size - 1; i += 1) {
    for (let j = 0; j < size - 1; j += 1) {
      const value = modules[i][j];
      if (
        value === modules[i][j + 1] &&
        value === modules[i + 1][j] &&
        value === modules[i + 1][j + 1]
      ) {
        penalty += 3;
      }
    }
  }

  // Rule 3: finder-like 1:1:3:1:1 patterns with a 4-module light margin.
  const finder = [true, false, true, true, true, false, true];
  const light4 = [false, false, false, false];
  const matches = (values: boolean[], at: number, pattern: boolean[]) =>
    pattern.every((bit, offset) => values[at + offset] === bit);

  for (let i = 0; i < size; i += 1) {
    const row = modules[i];
    const col = modules.map((line) => line[i]);

    for (const line of [row, col]) {
      for (let j = 0; j <= size - 7; j += 1) {
        if (!matches(line, j, finder)) continue;
        const before = j >= 4 && matches(line, j - 4, light4);
        const after = j + 11 <= size && matches(line, j + 7, light4);
        if (before || after) penalty += 40;
      }
    }
  }

  // Rule 4: deviation from a 50% dark ratio.
  for (const row of modules) {
    for (const value of row) if (value) darkCount += 1;
  }
  const ratio = (darkCount * 100) / (size * size);
  penalty += Math.floor(Math.abs(ratio - 50) / 5) * 10;

  return penalty;
}

/**
 * Encodes `text` as a QR matrix (no quiet zone; the renderer adds it).
 * Throws when the text is empty or too long for the supported versions.
 */
export function encodeQR(text: string): QRMatrix {
  if (!text) throw new Error("[qr] Cannot encode an empty payload.");

  const payload = new TextEncoder().encode(text);
  const version = pickVersion(payload.length);
  const size = version * 4 + 17;

  const base = createGrid(size);
  placeFinderPattern(base, 0, 0);
  placeFinderPattern(base, 0, size - 7);
  placeFinderPattern(base, size - 7, 0);
  placeAlignmentPatterns(base, version);
  placeTimingPatterns(base);

  // Snapshot of which modules are function patterns, taken before the
  // format/version areas are reserved so they can be reserved again per mask.
  const reserved = createGrid(size);
  reserved.cells = base.cells.map((row) => [...row]);
  reserveFormatAreas(reserved, version);

  const codewords = interleaveCodewords(
    buildDataCodewords(payload, version),
    version,
  );

  const withData = createGrid(size);
  withData.cells = reserved.cells.map((row) => [...row]);
  placeDataBits(withData, codewords);

  const isFunctionModule = (row: number, col: number) =>
    reserved.cells[row][col] !== null;

  let best: boolean[][] | null = null;
  let bestPenalty = Number.POSITIVE_INFINITY;

  for (let mask = 0; mask < MASK_COUNT; mask += 1) {
    const candidate = createGrid(size);
    candidate.cells = withData.cells.map((row, rowIndex) =>
      row.map((value, colIndex) => {
        const dark = value === true;
        if (isFunctionModule(rowIndex, colIndex)) return dark;
        return isMasked(mask, rowIndex, colIndex) ? !dark : dark;
      }),
    );

    placeFormatInformation(candidate, mask);
    placeVersionInformation(candidate, version);

    const modules = candidate.cells.map((row) => row.map((v) => v === true));
    const penalty = maskPenalty(modules);
    if (penalty < bestPenalty) {
      bestPenalty = penalty;
      best = modules;
    }
  }

  if (!best) throw new Error("[qr] Failed to select a mask pattern.");

  return { size, modules: best };
}
